/**
 * Parser for Feynman DSL.
 * Converts tokens into an Abstract Syntax Tree (AST).
 */

import { Tokenizer, Token, TokenType } from './tokenizer';
import {
  ParseError,
  AnimationSequence,
  Participant,
  Step,
  Directive,
  Interaction,
  DirectiveType,
  ArrowType,
  Note,
  NotePosition,
  Fragment,
  FragmentType,
} from './types';
import type {
  AnimationDocument,
  NamedAnimationSequence,
  ParseResult,
} from './hierarchical-types';

/**
 * Recursive descent parser for Feynman DSL.
 */
export class Parser {
  private tokens: Token[];
  private current = 0;

  constructor(source: string) {
    const tokenizer = new Tokenizer(source);
    this.tokens = tokenizer.tokenize();
  }

  /**
   * Parses the entire source and returns the AST.
   *
   * @returns Complete animation sequence AST
   * @throws {ParseError} When syntax is invalid
   */
  public parse(): AnimationSequence {
    return this.parseAnimateSequence();
  }

  /**
   * Parses a document with potentially multiple animateSequence blocks.
   * Builds anchor map and validates references.
   *
   * @returns ParseResult with document, warnings, and errors
   */
  public parseDocument(): ParseResult {
    const sequences = new Map<string, AnimationSequence>();
    const anchors = new Map<string, string>();
    const warnings: string[] = [];
    const errors: string[] = [];
    let rootSequenceId: string | undefined;

    this.skipNewlines();

    // Parse all animateSequence blocks
    while (!this.isAtEnd()) {
      if (this.check('ANIMATE_SEQUENCE')) {
        const sequence = this.parseAnimateSequence();

        // Store sequence
        sequences.set(sequence.id, sequence);

        // Store anchor if defined
        if (sequence.anchorName) {
          if (anchors.has(sequence.anchorName)) {
            warnings.push(
              `Duplicate anchor definition: &${sequence.anchorName} (line ${this.peek().line})`
            );
          }
          anchors.set(sequence.anchorName, sequence.id);
        }

        // Last sequence is the root
        rootSequenceId = sequence.id;

        this.skipNewlines();
      } else if (this.check('NEWLINE')) {
        this.advance();
      } else {
        this.error(`Unexpected token: ${this.peek().type}`);
      }
    }

    // Validate anchor references
    for (const sequence of sequences.values()) {
      for (const participant of sequence.participants) {
        if (participant.name.startsWith('*')) {
          const anchorRef = participant.name.substring(1);
          if (!anchors.has(anchorRef)) {
            errors.push(
              `Undefined anchor reference: *${anchorRef} in sequence "${sequence.title}"`
            );
          }
        }
      }
    }

    const document: AnimationDocument = {
      sequences,
      anchors,
      rootSequenceId: rootSequenceId ?? undefined,
    };

    return {
      document,
      warnings,
      errors,
    };
  }

  /**
   * Parses an animateSequence block.
   * Supports optional anchor definition: animateSequence "Title" &anchorName
   */
  private parseAnimateSequence(): NamedAnimationSequence {
    this.expect('ANIMATE_SEQUENCE', 'Expected animateSequence keyword');
    this.skipNewlines();

    const title = this.expect('STRING', 'Expected string for title').value;
    this.skipNewlines();

    // Optional anchor definition: &anchorName
    let anchorName: string | undefined;
    if (this.check('AMPERSAND')) {
      this.advance(); // consume &
      const anchorToken = this.expect(
        'IDENTIFIER',
        'Expected identifier after & for anchor name'
      );
      anchorName = anchorToken.value;
      this.skipNewlines();
    }

    const participants: Participant[] = [];
    const steps: Step[] = [];

    // Parse participants and steps until we hit 'end'
    while (!this.check('END') && !this.isAtEnd()) {
      if (this.check('PARTICIPANT')) {
        participants.push(this.parseParticipant());
      } else if (this.check('STEP')) {
        steps.push(this.parseStep());
      } else if (this.check('NEWLINE')) {
        this.advance();
      } else {
        this.error(`Unexpected token: ${this.peek().type}`);
      }
    }

    this.expect('END', 'Expected end keyword');

    // Generate unique ID for this sequence
    const id = `seq-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    return {
      id,
      title,
      participants,
      steps,
      anchorName: anchorName ?? undefined,
    };
  }

  /**
   * Parses a participant declaration.
   * Supports anchor references: participant *anchorName
   */
  private parseParticipant(): Participant {
    this.expect('PARTICIPANT');
    this.skipNewlines();

    // Check for anchor reference (*anchorName)
    if (this.check('ASTERISK')) {
      this.advance(); // consume *
      const anchorRef = this.expect(
        'IDENTIFIER',
        'Expected identifier after * for anchor reference'
      ).value;
      this.skipNewlines();

      // Store anchor reference in name with * prefix for now
      // Will be resolved later when building document
      return { name: `*${anchorRef}` };
    }

    // Regular participant name
    const name = this.expect(
      'IDENTIFIER',
      'Expected identifier for participant name'
    ).value;
    this.skipNewlines();

    // Parse optional color attribute: (color=#hex)
    let color: string | undefined = undefined;
    if (this.check('LPAREN')) {
      color = this.parseParticipantColor();
      this.skipNewlines();
    }

    return { name, ...(color !== undefined && { color }) };
  }

  /**
   * Parses participant color attribute: (color=#hex)
   * Returns color value as string
   */
  private parseParticipantColor(): string {
    this.expect('LPAREN', 'Expected (');

    // Expect "color"
    const attrToken = this.expect('IDENTIFIER', 'Expected "color"');
    if (attrToken.value !== 'color') {
      this.error('Expected "color" attribute');
    }

    this.expect('EQUALS', 'Expected = after color');

    // Parse color value (e.g., #ff6b6b, #f00)
    const colorValue = this.expect(
      'IDENTIFIER',
      'Expected color value (e.g., #ff6b6b)'
    ).value;

    // Validate hex color format
    if (
      !colorValue.match(/^#[0-9a-fA-F]{3}$/) &&
      !colorValue.match(/^#[0-9a-fA-F]{6}$/)
    ) {
      this.error('Invalid color format. Expected #rgb or #rrggbb');
    }

    this.expect('RPAREN', 'Expected )');

    return colorValue;
  }

  /**
   * Parses a step block.
   */
  private parseStep(): Step {
    this.expect('STEP');
    this.skipNewlines();

    const description = this.expect(
      'STRING',
      'Expected string for step description'
    ).value;
    this.skipNewlines();

    // Parse optional attributes: (duration=1s) or (color=#hex)
    let duration: number | undefined = undefined;
    let color: string | undefined = undefined;
    if (this.check('LPAREN')) {
      const attrs = this.parseStepAttributes();
      duration = attrs.duration;
      color = attrs.color;
      this.skipNewlines();
    }

    const directives: Directive[] = [];
    const interactions: Interaction[] = [];
    const notes: Note[] = [];
    const fragments: Fragment[] = [];

    // Parse directives, interactions, notes, and fragments until we hit 'end'
    while (!this.check('END') && !this.isAtEnd()) {
      if (this.isDirective()) {
        directives.push(this.parseDirective());
      } else if (this.check('NOTE')) {
        notes.push(this.parseNote());
      } else if (this.isFragment()) {
        fragments.push(this.parseFragment());
      } else if (this.check('IDENTIFIER') || this.check('ASTERISK')) {
        // Interaction starts with an identifier or asterisk (anchor reference)
        interactions.push(this.parseInteraction());
      } else if (this.check('NEWLINE')) {
        this.advance();
      } else {
        this.error(`Unexpected token in step: ${this.peek().type}`);
      }
    }

    this.expect('END', 'Expected end keyword for step');
    this.skipNewlines();

    return {
      description,
      directives,
      interactions,
      notes,
      fragments,
      ...(duration !== undefined && { duration }),
      ...(color !== undefined && { color }),
    };
  }

  /**
   * Parses step attributes: (duration=1s) or (color=#hex)
   * Returns object with optional duration (in milliseconds) and color
   */
  private parseStepAttributes(): { duration?: number; color?: string } {
    this.expect('LPAREN', 'Expected (');
    this.skipNewlines();

    const result: { duration?: number; color?: string } = {};

    // Parse multiple attributes separated by spaces/newlines
    while (!this.check('RPAREN') && !this.isAtEnd()) {
      // Parse attribute name
      const attrToken = this.expect('IDENTIFIER', 'Expected attribute name');
      const attrName = attrToken.value;

      this.expect('EQUALS', 'Expected = after attribute name');

      if (attrName === 'duration') {
        // Parse duration value (e.g., "1s", "500ms", "2.5s")
        const valueToken = this.peek();
        let durationMs: number;

        if (valueToken.type === 'NUMBER') {
          // Parse number with unit from NUMBER token (e.g., "1s", "500ms")
          const value = this.advance().value;
          const match = value.match(/^(\d+(?:\.\d+)?)(ms|s)$/);

          if (!match) {
            this.error('Invalid duration format. Use "1s" or "500ms"');
          }

          const [, numStr = '', unit = 'ms'] = match;
          const num = parseFloat(numStr);

          if (unit === 's') {
            durationMs = num * 1000;
          } else {
            durationMs = num;
          }
        } else {
          this.error('Expected duration value (e.g., 1s, 500ms)');
        }

        result.duration = durationMs;
      } else if (attrName === 'color') {
        // Parse color value (e.g., #ff6b6b, #f00)
        const colorValue = this.expect(
          'IDENTIFIER',
          'Expected color value (e.g., #ff6b6b)'
        ).value;

        // Validate hex color format
        if (
          !colorValue.match(/^#[0-9a-fA-F]{3}$/) &&
          !colorValue.match(/^#[0-9a-fA-F]{6}$/)
        ) {
          this.error('Invalid color format. Expected #rgb or #rrggbb');
        }

        result.color = colorValue;
      } else {
        this.error(
          `Unknown attribute: ${attrName}. Expected 'duration' or 'color'`
        );
      }

      // Skip whitespace/newlines between attributes
      this.skipNewlines();
    }

    this.expect('RPAREN', 'Expected )');
    return result;
  }

  /**
   * Parses a directive (focus, dim, show, hide).
   */
  private parseDirective(): Directive {
    const token = this.advance();
    const type = token.type.toLowerCase() as DirectiveType;

    this.expect('COLON', 'Expected colon after directive');
    this.skipNewlines();

    const participants: string[] = [];

    // Parse comma-separated participant names
    if (!this.check('IDENTIFIER')) {
      this.error('Expected at least one participant in directive');
    }

    participants.push(this.expect('IDENTIFIER').value);
    this.skipNewlines();

    while (this.check('COMMA')) {
      this.advance(); // consume comma
      this.skipNewlines();
      participants.push(this.expect('IDENTIFIER').value);
      this.skipNewlines();
    }

    this.skipNewlines();

    return {
      type,
      participants,
    };
  }

  /**
   * Parses an interaction (message between participants).
   * Supports anchor references: User -> *service: "message"
   */
  private parseInteraction(): Interaction {
    // Parse 'from' participant (regular identifier or anchor reference)
    let from: string;
    if (this.check('ASTERISK')) {
      this.advance(); // consume *
      from = '*' + this.expect('IDENTIFIER').value;
    } else {
      from = this.expect('IDENTIFIER').value;
    }
    this.skipNewlines();

    const arrow = this.expect('ARROW', 'Expected arrow').value as ArrowType;
    this.skipNewlines();

    // Parse 'to' participant (regular identifier or anchor reference)
    let to: string;
    if (this.check('ASTERISK')) {
      this.advance(); // consume *
      to = '*' + this.expect('IDENTIFIER').value;
    } else {
      to = this.expect('IDENTIFIER').value;
    }
    this.skipNewlines();

    this.expect('COLON', 'Expected colon after arrow');
    this.skipNewlines();

    const message = this.expect('STRING', 'Expected string for message').value;
    this.skipNewlines();

    return {
      from,
      to,
      message,
      arrow,
    };
  }

  /**
   * Parses a note annotation.
   * Syntax: note over: Participant1, Participant2: "Note text"
   *         note left: Participant: "Note text"
   *         note right: Participant: "Note text"
   */
  private parseNote(): Note {
    this.expect('NOTE', 'Expected note keyword');
    this.skipNewlines();

    // Parse position (over, left, right)
    const positionToken = this.advance();
    let position: NotePosition;

    if (positionToken.type === 'OVER') {
      position = 'over';
    } else if (positionToken.type === 'LEFT') {
      position = 'left';
    } else if (positionToken.type === 'RIGHT') {
      position = 'right';
    } else {
      this.error('Expected over, left, or right after note');
    }
    this.skipNewlines();

    this.expect('COLON', 'Expected colon after note position');
    this.skipNewlines();

    // Parse comma-separated participant names
    const participants: string[] = [];

    if (!this.check('IDENTIFIER') && !this.check('ASTERISK')) {
      this.error('Expected at least one participant in note');
    }

    // Handle first participant (could be anchor reference)
    if (this.check('ASTERISK')) {
      this.advance(); // consume *
      participants.push('*' + this.expect('IDENTIFIER').value);
    } else {
      participants.push(this.expect('IDENTIFIER').value);
    }
    this.skipNewlines();

    // Parse remaining participants
    while (this.check('COMMA')) {
      this.advance(); // consume comma
      this.skipNewlines();

      if (this.check('ASTERISK')) {
        this.advance(); // consume *
        participants.push('*' + this.expect('IDENTIFIER').value);
      } else {
        participants.push(this.expect('IDENTIFIER').value);
      }
      this.skipNewlines();
    }

    this.expect('COLON', 'Expected colon before note text');
    this.skipNewlines();

    const text = this.expect('STRING', 'Expected string for note text').value;
    this.skipNewlines();

    return {
      position,
      participants,
      text,
    };
  }

  /**
   * Parses a fragment block (alt, opt, loop, par).
   * Syntax: fragment alt: "condition"
   *           A -> B: "message"
   *         end
   */
  private parseFragment(): Fragment {
    this.expect('FRAGMENT', 'Expected fragment keyword');
    this.skipNewlines();

    // Parse fragment type (alt, opt, loop, par)
    const typeToken = this.advance();
    let type: FragmentType;

    if (typeToken.type === 'ALT') {
      type = 'alt';
    } else if (typeToken.type === 'OPT') {
      type = 'opt';
    } else if (typeToken.type === 'LOOP') {
      type = 'loop';
    } else if (typeToken.type === 'PAR') {
      type = 'par';
    } else {
      this.error('Expected alt, opt, loop, or par after fragment');
    }
    this.skipNewlines();

    this.expect('COLON', 'Expected colon after fragment type');
    this.skipNewlines();

    const label = this.expect(
      'STRING',
      'Expected string for fragment label'
    ).value;
    this.skipNewlines();

    const interactions: Interaction[] = [];

    // Parse interactions until we hit 'end'
    while (!this.check('END') && !this.isAtEnd()) {
      if (this.check('IDENTIFIER') || this.check('ASTERISK')) {
        interactions.push(this.parseInteraction());
      } else if (this.check('NEWLINE')) {
        this.advance();
      } else {
        this.error(`Unexpected token in fragment: ${this.peek().type}`);
      }
    }

    this.expect('END', 'Expected end keyword for fragment');
    this.skipNewlines();

    return {
      type,
      label,
      interactions,
    };
  }

  /**
   * Checks if current token is a directive keyword.
   */
  private isDirective(): boolean {
    return (
      this.check('FOCUS') ||
      this.check('DIM') ||
      this.check('SHOW') ||
      this.check('HIDE') ||
      this.check('ACTIVATE') ||
      this.check('DEACTIVATE')
    );
  }

  /**
   * Checks if current token is a fragment keyword.
   */
  private isFragment(): boolean {
    return this.check('FRAGMENT');
  }

  /**
   * Expects a specific token type and consumes it.
   *
   * @param type - Expected token type
   * @param message - Error message if expectation fails
   * @returns The consumed token
   * @throws {ParseError} When token doesn't match expectation
   */
  private expect(type: TokenType, message?: string): Token {
    if (this.check(type)) {
      return this.advance();
    }

    const token = this.peek();
    this.error(
      message ?? `Expected ${type} but got ${token.type}`,
      token.line,
      token.column
    );
  }

  /**
   * Checks if current token matches the given type.
   */
  private check(type: TokenType): boolean {
    if (this.isAtEnd()) {
      return false;
    }
    return this.peek().type === type;
  }

  /**
   * Advances to the next token and returns the previous one.
   */
  private advance(): Token {
    if (!this.isAtEnd()) {
      this.current++;
    }
    return this.previous();
  }

  /**
   * Returns current token without consuming it.
   */
  private peek(): Token {
    const token = this.tokens[this.current];
    if (token === undefined) {
      // Safety fallback - should never happen in practice
      const lastToken = this.tokens[this.tokens.length - 1];
      if (lastToken === undefined) {
        throw new Error('No tokens available');
      }
      return lastToken;
    }
    return token;
  }

  /**
   * Returns previous token.
   */
  private previous(): Token {
    const token = this.tokens[this.current - 1];
    if (token === undefined) {
      // Safety fallback - should never happen in practice
      const firstToken = this.tokens[0];
      if (firstToken === undefined) {
        throw new Error('No tokens available');
      }
      return firstToken;
    }
    return token;
  }

  /**
   * Checks if we've reached the end of tokens.
   */
  private isAtEnd(): boolean {
    return this.peek().type === 'EOF';
  }

  /**
   * Skips any newline tokens.
   */
  private skipNewlines(): void {
    while (this.check('NEWLINE')) {
      this.advance();
    }
  }

  /**
   * Throws a parse error with location information.
   */
  private error(message: string, line?: number, column?: number): never {
    const token = this.peek();
    throw new ParseError(message, line ?? token.line, column ?? token.column);
  }
}
